import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { motion } from "framer-motion"
import {
    Wifi,
    Projector,
    Snowflake,
    ShowerHead,
    Refrigerator,
    ChevronDown,
    ArrowRight,
} from "lucide-react"
import roomCam from "@/assets/rooms/room-1.jpg"
import roomQuyt from "@/assets/rooms/room-2.jpg"
import roomChanh from "@/assets/rooms/room-3.jpg"
import camSlogan from "@/assets/branding/cam-slogan.png"
import iconSao from "@/assets/branding/icon-sao.png"

// ─── Mock data (tạm thời — chưa nối API) ─────────────────────────
const MOCK_BRANCHES = ["Vĩnh Long", "Cần Thơ", "Sa Đéc"]

const AMENITIES = [
    { icon: Wifi, label: "WiFi miễn phí" },
    { icon: Projector, label: "Máy chiếu" },
    { icon: Snowflake, label: "Điều hòa" },
    { icon: ShowerHead, label: "Vòi sen nóng lạnh" },
    { icon: Refrigerator, label: "Tủ lạnh trong phòng" },
] as const

const PRICE_LIST = [
    { label: "3h", value: "111k" },
    { label: "Qua Đêm", value: "222k" },
    { label: "Full ngày", value: "333k" },
]

type MockRoom = {
    id: string
    name: string
    image: string
    /** Câu mô tả ngắn — hiển thị thay cho giá nếu có */
    tagline?: string
    prices?: { label: string; value: string }[]
    vip?: boolean
}

const MOCK_ROOMS: MockRoom[] = [
    { id: "cam", name: "Phòng Cam", image: roomCam, tagline: "Căn phòng mang vibe đà lạt" },
    { id: "quyt", name: "Phòng Quýt", image: roomQuyt, prices: PRICE_LIST, vip: true },
    { id: "chanh", name: "Phòng Chanh", image: roomChanh, prices: PRICE_LIST },
]

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
}

export function RoomSystemSection({ id }: { id?: string }) {
    const navigate = useNavigate()

    return (
        <section id={id} className="relative scroll-mt-24 overflow-hidden bg-[#fffdf5] py-12 lg:py-16">
            {/* Slogan "cam" trang trí bên trái (ảnh) */}
            <img
                src={camSlogan}
                aria-hidden="true"
                alt=""
                className="pointer-events-none absolute left-5 top-1/2 hidden h-[65%] w-auto -translate-y-1/2 select-none object-contain lg:block"
            />

            <div className="relative mx-auto max-w-[1240px] px-6 lg:pl-24 lg:pr-10">
                {/* Header row */}
                <div className="mb-8 flex flex-wrap items-center gap-4">
                    <h2 className="font-display text-2xl font-bold tracking-[0.04em] text-[#615b48] sm:text-[30px]">
                        HỆ THỐNG PHÒNG
                    </h2>

                    <div className="order-3 w-full sm:order-none sm:mx-auto sm:w-auto">
                        <BranchDropdown />
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/phong-nghi")}
                        className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[#be7622] transition-colors hover:text-[#c2762f]"
                    >
                        Xem tất cả
                        <ArrowRight className="size-4" />
                    </button>

                    <img
                        src={iconSao}
                        aria-hidden="true"
                        alt=""
                        className="hidden size-12 shrink-0 select-none object-contain lg:block lg:-mr-16 lg:mt-2"
                    />
                </div>

                {/* Cards */}
                <motion.div
                    className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15 }}
                    variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
                >
                    {MOCK_ROOMS.map((room) => (
                        <RoomCard key={room.id} room={room} onBook={() => navigate("/dat-phong")} />
                    ))}
                </motion.div>
            </div>
        </section>
    )
}

// ─── Room card ───────────────────────────────────────────────────
function RoomCard({ room, onBook }: { room: MockRoom; onBook: () => void }) {
    return (
        <motion.div variants={cardVariants} className="group cursor-pointer" onClick={onBook}>
            {/* Image */}
            <div className="relative overflow-hidden rounded-lg shadow-sm">
                {room.vip && (
                    <span className="absolute left-2 top-2 z-10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white shadow">
                        ⭐️Vip
                    </span>
                )}
                <img
                    src={room.image}
                    alt={room.name}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
            </div>

            {/* Name */}
            <h3 className="mt-3 text-lg font-bold text-[#3d342a]">{room.name}</h3>

            {/* Tan bar: tagline hoặc giá */}
            <div className="mt-1.5 rounded bg-[#d6cdb9] px-3 py-1.5 text-[13px] text-white font-bold">
                {room.tagline ? (
                    <span className="font-medium text-white">{room.tagline}</span>
                ) : (
                    <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-white">
                        {room.prices?.map((p) => (
                            <span key={p.label}>
                                <span className="opacity-90">{p.label}: </span>
                                <span className="font-semibold">{p.value}</span>
                            </span>
                        ))}
                    </span>
                )}
            </div>

            {/* Amenities */}
            <div className="mt-2.5 flex flex-wrap gap-2">
                {AMENITIES.map(({ icon: Icon, label }) => (
                    <span
                        key={label}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#eceef2] bg-[#f9fafc] px-3 py-1.5 text-xs font-medium text-[#6f6555]"
                    >
                        <Icon className="size-4 text-[#b9895c]" strokeWidth={2} />
                        {label}
                    </span>
                ))}
            </div>
        </motion.div>
    )
}

// ─── Branch dropdown (mock) ──────────────────────────────────────
function BranchDropdown() {
    const [selected, setSelected] = useState(MOCK_BRANCHES[0])
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener("mousedown", handler)
        return () => document.removeEventListener("mousedown", handler)
    }, [open])

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-center gap-3 rounded-full border border-[#e4d8c3] bg-[#faf5ea] px-5 py-2.5 shadow-sm transition-colors hover:bg-[#f5edda] sm:w-auto"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#a89a82]">
                    Chọn chi nhánh
                </span>
                <span className="text-sm font-semibold uppercase text-[#3d342a]">{selected}</span>
                <ChevronDown
                    className={`size-4 text-[#a89a82] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <ul
                    role="listbox"
                    className="absolute left-1/2 z-30 mt-2 w-52 -translate-x-1/2 overflow-hidden rounded-xl border border-black/5 bg-white py-1 shadow-xl"
                >
                    {MOCK_BRANCHES.map((branch) => (
                        <li key={branch} role="option" aria-selected={branch === selected}>
                            <button
                                type="button"
                                onClick={() => {
                                    setSelected(branch)
                                    setOpen(false)
                                }}
                                className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors hover:bg-[#f6f0e4] ${branch === selected ? "bg-[#f6f0e4] text-[#3d342a]" : "text-[#6f6555]"
                                    }`}
                            >
                                {branch}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
